import React, { memo, useCallback } from 'react'
import { ControlledSelect } from 'transfix-form/components'
import ControlledDatePicker from 'transfix-form/components/ControlledDatePicker'
import ControlledTextInput from 'transfix-form/components/ControlledTextInput'
import { FormModal, IFormModalProps } from 'transfix-ui/components/Modals/FormModal'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import { makeStyles } from 'transfix-ui/core/styles'
import { ICertificationCode } from 'types/FMSGraphqlTypes'
import { TEST_IDS } from './constants'
import { IUseCertificationFormProps, useCertificationForm } from './hooks/useCertificationForm'
import { TCertificationSchema, getErrorMessage } from './schema/CertificationSchema'
import { TCarrierCertificationsSubmitHandler, TCarrierCertificationsDeleteHandler } from './types'
import { getCertificationOptions } from './utils'
import { getCertificationTitleOptions } from './utils/getCertificationTitleOptions'

const useCertificationModalStyles = makeStyles(theme => ({
  deleteButton: {
    margin: 0,
    position: 'absolute',
    left: theme.spacing(3),
  },
}))

export interface ICertificationModalProps extends Pick<IFormModalProps, 'onClose'> {
  existingCertificationCodes: ICertificationCode[]
  onSubmit: TCarrierCertificationsSubmitHandler<TCertificationSchema>
  onDelete: TCarrierCertificationsDeleteHandler
  loading: boolean
  /**
   * @default {}
   */
  defaultValues?: IUseCertificationFormProps['defaultValues']
}

const CertificationModal = memo<ICertificationModalProps>(
  ({ defaultValues = {}, onClose, existingCertificationCodes, onSubmit, onDelete, loading }) => {
    const classes = useCertificationModalStyles()

    const isEditing = !!defaultValues.code
    const formTitle = `${isEditing ? 'Edit' : 'Add'} certification`
    const certificationTitleOptions = getCertificationTitleOptions()
    const certificationOptions = getCertificationOptions({
      isEditing,
      existingCertificationCodes,
      certificationTitleOptions,
    })

    const { control, errors, handleSubmit } = useCertificationForm({ defaultValues })
    const codeErrors = getErrorMessage(errors, 'code')
    const issuingOrganizationErrors = getErrorMessage(errors, 'issuingOrganization')
    const renewalDateErrors = getErrorMessage(errors, 'renewalDate')

    const submitHandler = useCallback(
      async (formValues: TCertificationSchema) => {
        const success = await onSubmit(formValues)
        if (success) {
          onClose()
        }
      },
      [onClose, onSubmit]
    )

    const deleteHandler = useCallback(async () => {
      const success = await onDelete()
      if (success) {
        onClose()
      }
    }, [onClose, onDelete])
    const deleteAction: IFormModalProps['actions'] = [
      {
        label: 'Delete',
        type: 'delete',
        action: deleteHandler,
        dataTest: TEST_IDS.certificationDeleteButton,
        BoxProps: { className: classes.deleteButton },
        disabled: loading,
      },
    ]

    const actions: IFormModalProps['actions'] = [
      ...(isEditing ? deleteAction : []),
      {
        label: 'Cancel',
        type: 'dismiss',
        action: onClose,
        disabled: loading,
      },
      {
        label: isEditing ? 'Update' : 'Add certification',
        type: 'confirm',
        action: handleSubmit(submitHandler),
        disabled: loading,
      },
    ]

    return (
      <FormModal
        title={formTitle}
        dataTest={TEST_IDS.certificationModal}
        actions={actions}
        onClose={onClose}
        open>
        <Box mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={7}>
              <ControlledSelect
                control={control}
                name='code'
                SelectProps={{
                  label: 'Certification Title',
                  placeholder: 'Select certification',
                  dataTest: TEST_IDS.certificationTitleSelect,
                  options: certificationOptions,
                  error: !!codeErrors,
                  validationMessage: codeErrors,
                  required: true,
                  disabled: isEditing,
                }}
              />
            </Grid>
            <Grid item xs={7}>
              <ControlledTextInput
                control={control}
                name='issuingOrganization'
                TextInputProps={{
                  label: 'Organization',
                  placeholder: 'Enter organization',
                  error: !!issuingOrganizationErrors,
                  validationMessage: issuingOrganizationErrors,
                  size: 'medium',
                  required: false,
                }}
              />
            </Grid>
            <Grid item xs={7}>
              <ControlledDatePicker
                control={control}
                name='renewalDate'
                DatePickerProps={{
                  label: 'Renewal Date',
                  placeholder: 'mm/dd/yyyy',
                  error: !!renewalDateErrors,
                  validationMessage: renewalDateErrors,
                  required: false,
                  clearable: true,
                  minDate: Date.now(),
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </FormModal>
    )
  }
)

export default CertificationModal
